<script lang="ts" setup>
import { computed, ref } from 'vue';

export interface ConfirmFooterButton {
    type: 'primary' | 'outline';
    text: string;
    dataId: string;
    isLoading?: boolean;
    isDisabled?: boolean;
    onClick: () => void | Promise<void>;
}

const props = withDefaults(
    defineProps<{
        modelValue: boolean;
        icon: string;
        iconStyle?: 'default' | 'red';
        header: string;
        /** Optional supporting copy under the title. Omit for form-only modals. */
        subHeader?: string;
        /** PS-1060: top-align the card and let the overlay scroll, instead of
         * vertically centering it. Set this for tall cards (multi-field forms) so
         * the footer stays reachable on short screens. NOTE: distinct from
         * AppModalLayout's `centerContent`, which controls text alignment. */
        alignTop?: boolean;
        /** Left-align the content slot instead of centring it, via AppModalLayout's
         * own `centerContent`. Use for form-only modals, whose fields and labels
         * read left-aligned. */
        alignContentLeft?: boolean;
        footerButtons: ConfirmFooterButton[];
    }>(),
    {
        iconStyle: 'default',
        alignTop: false,
        alignContentLeft: false
    }
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
}>();

const appModalLayoutFormat = ref<string>('center');

const isModalOpen = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit('update:modelValue', value)
});

const closeModal = () => {
    isModalOpen.value = false;
};
</script>

<template>
    <app-modal
        :is-open="isModalOpen"
        :center-content="!alignTop"
        @onClose="closeModal">
        <app-modal-layout
            :title="header"
            :icon="icon"
            :icon-style="iconStyle"
            :show-close="false"
            :center-content="!alignContentLeft"
            :footer-format="appModalLayoutFormat">

            <p v-if="subHeader" class="confirm-modal__description">
                {{ subHeader }}
            </p>

            <slot name="form-elements" />

            <template #footer-buttons>
                <app-button
                    v-for="btn in footerButtons"
                    :key="btn.dataId"
                    :type="btn.type"
                    fixed-width
                    size="large"
                    :is-loading="btn.isLoading"
                    :is-disabled="btn.isDisabled"
                    :data-id="btn.dataId"
                    @onClick="btn.onClick">
                    {{ btn.text }}
                </app-button>
            </template>
        </app-modal-layout>
    </app-modal>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.confirm-modal {
    &__description {
        font-size: 1rem;
        line-height: 1.5;
        color: $black-med;
        margin: 0;
    }
}
</style>
